// Hooks dos lembretes das notas (passo 18, PR 2).
//
// ⚠️ Arquivo próprio, e não `useChat.ts`: os testes da `ChatPage` trocam `@/hooks/useChat` por factory explícita.
// As chaves vêm de `lib/lembretes/cache.ts` (cópia própria), nunca de `./useChat`.
import { useMemo } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import lembretesService from "@/services/lembretesService";
import {
  CHAVE_DO_PAINEL,
  chaveDasMensagens,
  comLembreteNaMensagem,
  ehListaDeSalas,
  lembreteVisivel,
  painelDepoisDe,
} from "@/lib/lembretes/cache";
import { RAIZ_DA_AGENDA } from "@/lib/calendar/chaves";
import type { ChatMessage, Lembrete, PainelDeLembretes } from "@/types/chat";

/** O painel "⏰ Lembretes": só busca com o painel aberto (a ChatPage o monta sempre). */
export function usePainelDeLembretes(aberto: boolean) {
  return useQuery({
    queryKey: CHAVE_DO_PAINEL,
    queryFn: () => lembretesService.painel(),
    enabled: aberto,
    staleTime: 5_000,
  });
}

/** Onde mora o lembrete: a sala de notas, a nota e o lembrete de agora (`null` = nenhum). */
export interface AlvoDoLembrete {
  roomId: string;
  messageId: string;
  lembrete: Lembrete | null;
}

export type AcaoDoLembrete = "concluir" | "adiar";

function aplicarNaNota(qc: QueryClient, alvo: AlvoDoLembrete, lembrete: Lembrete | null) {
  qc.setQueryData<ChatMessage[]>(chaveDasMensagens(alvo.roomId), (old) =>
    comLembreteNaMensagem(old, alvo.messageId, lembrete)
  );
}

function aplicarNoPainel(qc: QueryClient, lembreteId: string | undefined, novo: Lembrete | null) {
  if (!lembreteId) return;
  qc.setQueryData<PainelDeLembretes>(CHAVE_DO_PAINEL, (old) =>
    painelDepoisDe(old, lembreteId, novo)
  );
}

function reconferir(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: CHAVE_DO_PAINEL });
  // Os números do botão "Lembretes" e da linha "Minhas notas" vêm da LISTA de salas (nunca do prefixo).
  void qc.invalidateQueries({ predicate: ehListaDeSalas });
  // A Agenda mostra os lembretes (PR 3) e não tem WebSocket: sem isto, ficava até 30 s velha.
  void qc.invalidateQueries({ queryKey: RAIZ_DA_AGENDA });
}

/**
 * Concluir ou adiar FORA da tela de mensagens (o aviso na tela, o `?acao=` do push): sem otimismo — o cartão
 * mostra o resultado do servidor —, mas a marca e o painel ficam certos se estiverem em cache.
 */
export async function acaoDoLembreteForaDaTela(
  qc: QueryClient,
  acao: AcaoDoLembrete,
  alvo: { id: string; message_id: string; room_id: string | null }
): Promise<Lembrete> {
  const novo =
    acao === "concluir"
      ? await lembretesService.concluir(alvo.id)
      : await lembretesService.adiar(alvo.id);
  // A nota vem da resposta (o `?acao=` do push só conhece o id do lembrete).
  if (alvo.room_id)
    aplicarNaNota(qc, { roomId: alvo.room_id, messageId: novo.message_id, lembrete: null }, novo);
  aplicarNoPainel(qc, alvo.id, novo);
  reconferir(qc);
  return novo;
}

/** As ações da bolha e do painel: a marca muda NA HORA (otimista) e volta se o servidor recusar (o erro sobe). */
export function useAcoesDoLembrete() {
  const qc = useQueryClient();
  return useMemo(() => {
    const comOtimismo = async (
      alvo: AlvoDoLembrete,
      provisorio: Lembrete | null,
      pedir: () => Promise<Lembrete | null>
    ): Promise<Lembrete | null> => {
      const antes = alvo.lembrete;
      const painelAntes = qc.getQueryData<PainelDeLembretes>(CHAVE_DO_PAINEL);
      aplicarNaNota(qc, alvo, provisorio);
      aplicarNoPainel(qc, antes?.id, provisorio);
      try {
        const certo = await pedir();
        aplicarNaNota(qc, alvo, certo);
        return certo;
      } catch (erro) {
        aplicarNaNota(qc, alvo, antes);
        qc.setQueryData(CHAVE_DO_PAINEL, painelAntes);
        // Repor `antes` pode contrariar o servidor: se um `lembrete:atualizado` de outra superfície chegou
        // durante o voo (é o 409), o cache das mensagens já tinha o estado novo e agora volta ao velho. O
        // `reconferir` refaz o painel e as listas, mas não a página de mensagens: sem isto a bolha ficaria
        // contrariando o servidor (botões que dão 409 de novo) até recarregar.
        void qc.invalidateQueries({ queryKey: chaveDasMensagens(alvo.roomId) });
        throw erro;
      } finally {
        reconferir(qc);
      }
    };
    const agora = () => new Date().toISOString();
    return {
      marcar: (alvo: AlvoDoLembrete, lembrarEm: string) =>
        comOtimismo(
          alvo,
          {
            id: alvo.lembrete?.id ?? "provisorio",
            message_id: alvo.messageId,
            lembrar_em: lembrarEm,
            status: "pendente",
            tocou_em: null,
            concluido_em: null,
            adiado_vezes: 0,
          },
          () => lembretesService.marcar(alvo.messageId, lembrarEm)
        ),
      cancelar: (alvo: AlvoDoLembrete) =>
        comOtimismo(alvo, null, async () => {
          await lembretesService.cancelar(alvo.messageId);
          return null;
        }),
      concluir: (alvo: AlvoDoLembrete) => {
        const l = lembreteVisivel(alvo.lembrete);
        if (!l) return Promise.resolve(null);
        return comOtimismo(alvo, { ...l, status: "concluido", concluido_em: agora() }, () =>
          lembretesService.concluir(l.id)
        );
      },
      adiar: (alvo: AlvoDoLembrete) => {
        const l = lembreteVisivel(alvo.lembrete);
        if (!l) return Promise.resolve(null);
        const daquiA10 = new Date(Date.now() + 10 * 60_000).toISOString();
        return comOtimismo(
          alvo,
          { ...l, status: "pendente", lembrar_em: daquiA10, adiado_vezes: l.adiado_vezes + 1 },
          () => lembretesService.adiar(l.id)
        );
      },
    };
  }, [qc]);
}
